export const defaultColor = '#10b981';

export const defaultColorArray: string[] = [
  '#4153B9',
  '#4DB251',
  '#00C2DB',
  '#2598F3',
  '#693BBA',
  '#9F28B4',
  '#EA2568',
  '#FF9A05',
  '#FFC20A',
];

export const getRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

export const trackSpeedColors = (trackColor: any) => {
  if (trackColor?.type === 'speed' && trackColor?.arrayColor?.length) {
    return trackColor?.arrayColor?.map((c: any) => ({
      ...c,
      minValue: c.minValue || -Infinity,
      maxValue: c.maxValue || Infinity,
    }));
  }
  return [
    { color: '#D32F2F', maxValue: 1, minValue: -Infinity },
    { color: '#388E3C', maxValue: +Infinity, minValue: 1 },
  ];
};

/** HSV: h — 0..360, s и v — 0..1. Внутреннее представление пикера. */
export interface Hsv {
  h: number;
  s: number;
  v: number;
}

const HEX_PATTERN = /^#?([\da-f]{3}|[\da-f]{6})$/i;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Приводит произвольную строку к `#RRGGBB` в верхнем регистре.
 * Понимает короткую форму `#abc` и запись без решётки. Мусор — `null`.
 */
export const normalizeHex = (value: string | null | undefined): string | null => {
  const match = value?.trim().match(HEX_PATTERN);

  if (!match) return null;

  const digits = match[1] as string;
  const full =
    digits.length === 3
      ? digits
          .split('')
          .map((char) => char + char)
          .join('')
      : digits;

  return `#${full.toUpperCase()}`;
};

export const hexToHsv = (hex: string): Hsv => {
  const normalized = normalizeHex(hex) ?? defaultColor;
  const r = parseInt(normalized.slice(1, 3), 16) / 255;
  const g = parseInt(normalized.slice(3, 5), 16) / 255;
  const b = parseInt(normalized.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const delta = max - Math.min(r, g, b);

  let h = 0;

  if (delta) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;

    h *= 60;
    if (h < 0) h += 360;
  }

  return { h, s: max ? delta / max : 0, v: max };
};

export const hsvToHex = ({ h, s, v }: Hsv): string => {
  const hue = ((h % 360) + 360) % 360;
  const saturation = clamp(s, 0, 1);
  const value = clamp(v, 0, 1);

  const channel = (offset: number) => {
    const k = (offset + hue / 60) % 6;
    const component = value * (1 - saturation * Math.max(0, Math.min(k, 4 - k, 1)));

    return Math.round(component * 255)
      .toString(16)
      .padStart(2, '0');
  };

  return `#${channel(5)}${channel(3)}${channel(1)}`.toUpperCase();
};

const RGB_PATTERN = /^rgba?\(([^)]*)\)$/i;

const CHANNEL_PATTERN = /^([+-]?(?:\d+\.?\d*|\.\d+))(%?)$/;

const channelToHex = (value: number) =>
  clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0');

/** Канал `rgb()` — число 0..255 или проценты. */
const parseChannel = (raw: string): number | null => {
  const match = raw.match(CHANNEL_PATTERN);

  if (!match) return null;

  const value = Number(match[1]);

  return match[2] ? (value / 100) * 255 : value;
};

/**
 * Приводит `rgb()`/`rgba()` к `#RRGGBB`. Понимает обе нотации — через запятые и через пробелы
 * с альфой за слэшем. Альфа отбрасывается: пикер работает без прозрачности.
 */
const rgbToHex = (value: string): string | null => {
  const match = value.match(RGB_PATTERN);

  if (!match) return null;

  const parts = match[1].split('/');

  if (parts.length > 2) return null;

  const channels = parts[0]
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean);

  // в записи через запятые альфа идёт четвёртым числом, а не за слэшем
  if (parts.length === 1 && channels.length === 4) channels.pop();

  if (channels.length !== 3) return null;

  const digits: string[] = [];

  for (const channel of channels) {
    const parsed = parseChannel(channel);

    if (parsed === null) return null;

    digits.push(channelToHex(parsed));
  }

  return `#${digits.join('')}`.toUpperCase();
};

/**
 * Приводит цвет к `#RRGGBB`: hex в любой форме либо `rgb()`/`rgba()` — бэкенд отдаёт легаси-записи
 * вида `rgba(37, 234, 243, 1)`. Мусор — `null`.
 */
export const parseColor = (value: string | null | undefined): string | null =>
  normalizeHex(value) ?? rgbToHex(value?.trim() ?? '');
